import { ProfilePicture, Tooltip } from '@getro/rombo';
import React from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';
import pluralize from 'pluralize';

export const ContactCompanyRenderer = ({ data, value }) => {
  const { colors } = useTheme();
  if (data?.options?.showLoader) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={160}
        viewBox="0 0 400 160"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
      >
        <rect x={value?.columnKey !== 'companyName' ? '20' : '47'} y="16" rx="3" ry="3" width="80" height="8" />
        {value?.columnKey === 'companyName' && <rect x="20" y="12" rx="0" ry="0" width="16" height="16" />}
      </ContentLoader>
    );
  }

  if (data?.original?.enrichmentStatus !== 'enriched') {
    return '';
  }

  const currentWorkExperience = value?.experiences?.[0];

  if (!currentWorkExperience) {
    return (
      <Flex p="8px" alignItems="center" sx={{ height: '100%' }}>
        <Text fontSize="14px" fontStyle="italic" color="text.placeholder">
          No current {value?.columnKey === 'companyName' ? 'company' : 'job title'}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex p="8px" alignItems="center" sx={{ height: '100%' }}>
      <Flex sx={{ gap: '8px', alignItems: 'center' }}>
        <Flex
          sx={{
            gap: '8px',
            alignItems: 'center',
            flexGrow: 1,
            textDecoration: 'none',
            width: '100%',
          }}
        >
          {value?.showLogo && (
            <Box width="24px" height="24px" flex="0 0 auto">
              <ProfilePicture
                sx={{
                  minWidth: '24px',
                  minHeight: '24px',
                }}
                variant="square"
                imageUrl={currentWorkExperience?.logoUrl ?? ''}
                name={currentWorkExperience?.[value?.columnKey] ?? ''}
              />
            </Box>
          )}
          <Text
            fontSize="14px"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentWorkExperience?.[value?.columnKey]}
          </Text>
        </Flex>

        {value?.experiences?.length > 1 && (
          <Tooltip
            position="top"
            content={`View ${value?.experiences?.length - 1} more current ${pluralize(
              value?.columnKey === 'companyName' ? 'company' : 'job title',
              value?.experiences?.length - 1,
            )}`}
            trigger={
              <Box
                as={Link}
                to={data?.options?.contactUrl}
                sx={{
                  flexShrink: 0,
                  cursor: 'pointer',
                  borderRadius: '99px',
                  border: '1px solid',
                  borderColor: 'border.subtle',
                  bg: 'neutral.0',
                  color: 'text.main',
                  textDecoration: 'none',
                  p: '2px 8px',
                  display: 'flex',
                  maxHeight: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text sx={{ flexShrink: 0 }}>+{value?.experiences?.length - 1}</Text>
              </Box>
            }
          />
        )}
      </Flex>
    </Flex>
  );
};

ContactCompanyRenderer.propTypes = {
  data: PropTypes.object.isRequired,
  value: PropTypes.string.isRequired,
};
